import { TTokenRedemptionOptionPrize } from '@daymade/api-contracts';
import LoadingOverlay from 'components/layout/LoadingOverlay';
import MainLayout from 'components/layout/MainLayout';
import Separator from 'components/Separator';
import ALittleLostSection from 'components/token-town/a-little-lost-section';
import ImageGallery from 'components/token-town/draws/image-gallery/ImageGallery';
import Header from 'components/token-town/header/Header';
import BackLink from 'components/token-town/links/BackLink';
import PageGrid from 'components/token-town/page-grid/PageGrid';
import Page from 'components/token-town/page/Page';
import { PaginatedList } from 'components/token-town/paginated-list/PaginatedList';
import PrizeRedemptionCard from 'components/token-town/prize-card/PrizeRedemptionCard';
import PrizeRedemptionDescription from 'components/token-town/prize-redemption-description/PrizeRedemptonDescription';
import PrizeRedemptionModal from 'components/token-town/prize-redemption-modal/PrizeRedemptionModal';
import PurchaseTokensBanner from 'components/token-town/purchase-tokens-banner/PurchaseTokensBanner';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useTokenRedemptionOption } from 'hooks/token-town/useTokenRedemptionOption';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { isDesktop, isTablet } from 'react-device-detect';
import { isClientSideNextJs } from 'utils/common';

const PrizeRedemptionPage = observer(() => {
  const [selectedPrize, setSelectedPrize] =
    useState<null | TTokenRedemptionOptionPrize>(null);

  const router = useRouter();
  const { slug: routerSlug } = router.query;
  const slug = Array.isArray(routerSlug) ? routerSlug[0] : routerSlug;

  const { currentUser, balances, updateCurrentUser } = useUserStore();
  const playerTokenCount = balances?.tokenBalance ?? 0;

  if (!slug) {
    if (isClientSideNextJs) {
      router?.replace('/404');
    }
  }

  const { loading, error, tokenRedemptionOption } =
    useTokenRedemptionOption(slug);

  useEffect(() => {
    const isMissingPrizeData =
      !loading &&
      (!tokenRedemptionOption || (!slug && !tokenRedemptionOption.prizes));

    if (isMissingPrizeData) {
      router.replace('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenRedemptionOption, loading]);

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!tokenRedemptionOption || !slug) {
    return null;
  }

  if (error) {
    return <>An error occurred: {error}</>;
  }

  return (
    <MainLayout>
      <Header />
      <Page>
        {selectedPrize && (
          <PrizeRedemptionModal
            tokenRedemptionOptionId={tokenRedemptionOption.id}
            tokenCost={tokenRedemptionOption.price ?? 0}
            closePopup={() => setSelectedPrize(null)}
            onSuccessfulChoiceHandler={async (id) => {
              await updateCurrentUser();
              setSelectedPrize(null);
              router.push(`/token-town/confirmation?id=${id}`);
            }}
            selectedPrize={selectedPrize}
          />
        )}
        {currentUser && <PurchaseTokensBanner tokenCount={playerTokenCount} />}
        <PageGrid splitEarly>
          <ImageGallery
            tokenCost={tokenRedemptionOption.price ?? 0}
            images={[tokenRedemptionOption.heroImage]}
          />

          {tokenRedemptionOption.title && (
            <PrizeRedemptionDescription title={tokenRedemptionOption.title} />
          )}
        </PageGrid>
        <PaginatedList
          items={tokenRedemptionOption.prizes || []}
          itemsPerPage={isDesktop || isTablet ? 6 : 4}
          renderItem={(prize: TTokenRedemptionOptionPrize) => (
            <PrizeRedemptionCard
              onClick={() => setSelectedPrize(prize)}
              prize={prize}
            />
          )}
        />
        <Separator margin={0} />
        <BackLink to="prizes" />
      </Page>
      <ALittleLostSection />

      <style jsx>{`
        .container {
          width: 100%;
        }

        .prizeList {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          width: 100%;
        }

        .pageControls {
          display: flex;
          justify-content: center;
          align-items: center;
          max-width: 1050px;
          margin: 1em auto;
          padding: 0 2em;
        }

        .pageCount {
          font-weight: 800;
          font-size: 1.2rem;
          margin: 0 9px;
        }
      `}</style>
    </MainLayout>
  );
});

export default PrizeRedemptionPage;
